import { Injectable } from '@angular/core';
import { hideLoader } from '@app/core/interceptors/interceptor-http-params';
import { DictionaryService } from '@app/core/services/dictionary.service';
import { GraphqlService } from '@app/core/services/graphql.service';
import { LocoTypeSearch } from '@app/pcs/administration/loco-types/models/loco-type-search';
import {
  copyLocoTypesMutation,
  deleteLocoTypesMutation,
  updateLocoTypeMutation
} from '@app/pcs/data/loco-type.mutations';
import {
  locoTypesForAgencyQuery,
  locoTypesSearchQuery,
  locoTypeUsedInDossiersQuery,
  validateLocoTypeNumberQuery,
  validateNationalIdQuery
} from '@app/pcs/data/loco-type.queries';
import { LocoType, PcsEntity } from '@app/pcs/models';
import { UserGridType } from '@app/pcs/models/user-grid-type';
import * as R from 'ramda';
import { map } from 'rxjs/operators';

@Injectable()
export class LocoTypeDataService {

  constructor(private readonly graphqlService: GraphqlService, private readonly dictionaryService: DictionaryService) {
  }

  fetchLocoTypes(searchTerm, ttpId, agencyId) {
    return this.graphqlService.query(locoTypesSearchQuery, {
      searchContext: {
        timetablePeriod: R.find(R.propEq('id', ttpId), this.dictionaryService.getTimetablePeriods()),
        agency: R.find(R.propEq('id', agencyId), this.dictionaryService.getAgencies()),
        searchTerm
      } as LocoTypeSearch
    }, hideLoader());
  }

  fetchLocoTypesForAgency(searchContext: LocoTypeSearch) {
    return this.graphqlService.query(locoTypesForAgencyQuery, {
      searchContext,
      referenceDataSearchContext: {
        ttpId: searchContext.timetablePeriod.id,
        imAgencyId: searchContext.agency.id,
        type: {id: PcsEntity.LocoType}
      },
      userGridType: {id: UserGridType.LocoTypes}
    }).pipe(map((response) => ({
      locoType: response[0],
      referenceDataEditRules: response[1],
      userGridConfiguration: response[2]
    })));
  }

  update(locoType: LocoType) {
    return this.graphqlService.mutation(updateLocoTypeMutation, {
      locoType
    });
  }

  copyLocoTypes(locoTypeIds: number[], ttpId: number) {
    return this.graphqlService.mutation(copyLocoTypesMutation, {
      locoTypeIds,
      ttpId
    });
  }

  delete(locoTypeIds: number[]) {
    return this.graphqlService.mutation(deleteLocoTypesMutation, {
      locoTypeIds
    });
  }

  validateLocoTypeNumber(locoType: LocoType) {
    return this.graphqlService.query(validateLocoTypeNumberQuery, {
      input: {
        locoTypeId: locoType.id,
        ttpId: locoType.ttpId,
        agency: locoType.agency,
        ...R.pickAll(['typeOfLoco', 'country', 'serialNumber', 'seriesNumber'], locoType.locoTypeNumber)
      }
    }, hideLoader());
  }

  validateNationalId(locoType: LocoType) {
    return this.graphqlService.query(validateNationalIdQuery, {
      input: {
        locoTypeId: locoType.id,
        ttpId: locoType.ttpId,
        nationalId: locoType.nationalId,
        agency: locoType.agency
      }
    }, hideLoader());
  }

  usedInDossiers(id: number) {
    return this.graphqlService.query(locoTypeUsedInDossiersQuery, {
      id
    });
  }
}
